import { useState, useMemo, useEffect } from 'react';
import type { MockNCR, MockInspection, MockCAPA, MockInspectionResult } from '@/mocks/qms';
import { getAllOperators } from '@/api/operators.api';
import { qcService } from '@/services/qcService';
import SearchableSelect from '@/components/base/SearchableSelect';
import { useToast } from '@/contexts/ToastContext';

interface Props {
  ncr: MockNCR | null;
  onClose: () => void;
  onStatusChange: (ncrId: string, newStatus: MockNCR['status']) => void;
  onUpdateNCR: (updated: MockNCR) => void;
  onRaiseCAPA: (ncr: MockNCR) => void;
  onVerifyCAPA: (ncr: MockNCR) => void;
}

interface OperatorState {
  id: string;
  name: string;
  employee_code: string;
  skill: string;
}

const severityBadge = (severity: MockNCR['severity']) => {
  const map: Record<string, string> = {
    MINOR: 'bg-sky-100 text-sky-700',
    MAJOR: 'bg-amber-100 text-amber-700',
    CRITICAL: 'bg-red-100 text-red-700',
  };
  return map[severity] || 'bg-gray-100 text-gray-700';
};

const statusBadge = (status: MockNCR['status']) => {
  const map: Record<string, string> = {
    OPEN: 'bg-red-100 text-red-700',
    UNDER_REVIEW: 'bg-amber-100 text-amber-700',
    CAPA_RAISED: 'bg-violet-100 text-violet-700',
    CAPA_VERIFIED: 'bg-blue-100 text-blue-700',
    CLOSED: 'bg-emerald-100 text-emerald-700',
  };
  return map[status] || 'bg-gray-100 text-gray-700';
};

const dispositionBadge = (action: MockNCR['dispositionAction']) => {
  const map: Record<string, string> = {
    REJECT: 'bg-red-100 text-red-700',
    REWORK: 'bg-amber-100 text-amber-700',
    USE_AS_IS: 'bg-emerald-100 text-emerald-700',
    RETURN_TO_SUPPLIER: 'bg-orange-100 text-orange-700',
    PENDING: 'bg-gray-100 text-gray-700',
  };
  return map[action] || 'bg-gray-100 text-gray-700';
};

const capaStatusBadge = (status: MockCAPA['status']) => {
  const map: Record<string, string> = {
    OPEN: 'bg-gray-100 text-gray-700',
    IN_PROGRESS: 'bg-blue-100 text-blue-700',
    COMPLETED: 'bg-emerald-100 text-emerald-700',
    VERIFIED: 'bg-violet-100 text-violet-700',
  };
  return map[status] || 'bg-gray-100 text-gray-700';
};

const rootCauseBadge = (cat: MockCAPA['rootCauseCategory']) => {
  const map: Record<string, string> = {
    MATERIAL: 'bg-orange-100 text-orange-700',
    MACHINE: 'bg-gray-100 text-gray-700',
    METHOD: 'bg-sky-100 text-sky-700',
    MANPOWER: 'bg-amber-100 text-amber-700',
    MEASUREMENT: 'bg-violet-100 text-violet-700',
    ENVIRONMENT: 'bg-emerald-100 text-emerald-700',
  };
  return map[cat] || 'bg-gray-100 text-gray-700';
};

export default function NCRDetailSlideOver({
  ncr,
  onClose,
  onStatusChange,
  onUpdateNCR,
  onRaiseCAPA,
  onVerifyCAPA,
}: Props) {
  const toast = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [editDisposition, setEditDisposition] = useState<MockNCR['dispositionAction']>('PENDING');
  const [editAssignedTo, setEditAssignedTo] = useState('');
  const [editDueDate, setEditDueDate] = useState('');
  const [editNotes, setEditNotes] = useState('');

  const [operators, setOperators] = useState<OperatorState[]>([]);

  useEffect(() => {
    let active = true;
    async function loadData() {
      try {
        const res = await getAllOperators();
        if (active && res.success && res.data) {
          setOperators(res.data);
        }
      } catch (err) {
        console.error('Failed to load operators in NCRDetailSlideOver:', err);
      }
    }
    loadData();
    return () => {
      active = false;
    };
  }, []);

  const [linkedInspection, setLinkedInspection] = useState<MockInspection | null>(null);
  const [failedParamResults, setFailedParamResults] = useState<MockInspectionResult[]>([]);
  const [linkedCAPA, setLinkedCAPA] = useState<MockCAPA | null>(null);

  useEffect(() => {
    if (!ncr) {
      setLinkedInspection(null);
      setFailedParamResults([]);
      setLinkedCAPA(null);
      return;
    }

    let active = true;

    async function fetchLinkedData() {
      if (ncr?.inspectionId) {
        try {
          const res = await qcService.getInspectionById(ncr.inspectionId);
          if (active && res) {
            setLinkedInspection(res.inspection || null);
            setFailedParamResults((res.results || []).filter((r) => r.status === 'FAIL'));
          }
        } catch {
          if (active) {
            setLinkedInspection(null);
            setFailedParamResults([]);
          }
        }
      } else {
        if (active) {
          setLinkedInspection(null);
          setFailedParamResults([]);
        }
      }

      try {
        const capas = await qcService.getCAPAs();
        if (active) {
          const match = (capas || []).find((c) => c.ncrId === ncr?.id);
          setLinkedCAPA(match || null);
        }
      } catch {
        if (active) setLinkedCAPA(null);
      }
    }

    fetchLinkedData();

    return () => {
      active = false;
    };
  }, [ncr]);

  const dispositionOptions = useMemo(() => [
    { value: 'REJECT', label: 'Reject' },
    { value: 'REWORK', label: 'Rework' },
    { value: 'USE_AS_IS', label: 'Use As Is' },
    { value: 'RETURN_TO_SUPPLIER', label: 'Return to Supplier' },
    { value: 'PENDING', label: 'Pending' },
  ], []);

  const operatorOptions = useMemo(() => [
    { value: '', label: 'Unassigned' },
    ...operators.map((o) => ({
      value: o.id,
      label: o.name,
      sublabel: o.skill || o.employee_code || undefined,
    })),
  ], [operators]);

  if (!ncr) return null;

  const startEdit = () => {
    setEditDisposition(ncr.dispositionAction);
    setEditAssignedTo(ncr.assignedTo || '');
    setEditDueDate(ncr.dueDate || '');
    setEditNotes('');
    setIsEditing(true);
  };

  const saveEdit = () => {
    const assignedOp = operators.find((o) => o.id === editAssignedTo);
    onUpdateNCR({
      ...ncr,
      dispositionAction: editDisposition,
      assignedTo: editAssignedTo || null,
      assignedToName: assignedOp?.name || null,
      dueDate: editDueDate || null,
    });
    setIsEditing(false);
    toast.success('NCR updated');
  };

  const nextStatusLabel: Record<string, string> = {
    OPEN: 'Start Review',
    UNDER_REVIEW: 'Raise CAPA',
    CAPA_RAISED: 'Verify CAPA',
    CAPA_VERIFIED: 'Close NCR',
  };

  const nextStatusMap: Record<string, MockNCR['status']> = {
    OPEN: 'UNDER_REVIEW',
    UNDER_REVIEW: 'CAPA_RAISED',
    CAPA_RAISED: 'CAPA_VERIFIED',
    CAPA_VERIFIED: 'CLOSED',
  };

  const canAdvance = ncr.status !== 'CLOSED';

  const handleAdvance = () => {
    if (ncr.status === 'UNDER_REVIEW') {
      onRaiseCAPA(ncr);
      return;
    }
    if (ncr.status === 'CAPA_RAISED') {
      onVerifyCAPA(ncr);
      return;
    }
    const next = nextStatusMap[ncr.status];
    if (next) {
      if (next === 'CLOSED') {
        onUpdateNCR({ ...ncr, status: next, closedDate: new Date().toISOString().split('T')[0] });
        toast.success('NCR closed');
      } else {
        onStatusChange(ncr.id, next);
      }
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex justify-end">
        <div className="absolute inset-0 bg-black/40" onClick={onClose} />
        <div className="relative bg-white w-full max-w-2xl h-full overflow-y-auto shadow-xl flex flex-col">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{ncr.ncrNumber}</h2>
              <div className="flex items-center gap-2 mt-1">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${severityBadge(ncr.severity)}`}>
                  {ncr.severity}
                </span>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge(ncr.status)}`}>
                  {ncr.status.replace(/_/g, ' ')}
                </span>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer">
              <i className="ri-close-line text-gray-500 text-lg" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Status Flow */}
            {canAdvance && (
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">Status Flow</p>
                <div className="flex items-center gap-2 flex-wrap">
                  {(['OPEN', 'UNDER_REVIEW', 'CAPA_RAISED', 'CAPA_VERIFIED', 'CLOSED'] as const).map(
                    (s, idx, arr) => {
                      const isActive = s === ncr.status;
                      const isPast =
                        arr.indexOf(s) < arr.indexOf(ncr.status);
                      return (
                        <div key={s} className="flex items-center gap-2">
                          <span
                            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                              isActive
                                ? statusBadge(s)
                                : isPast
                                ? 'bg-emerald-50 text-emerald-600'
                                : 'bg-gray-100 text-gray-400'
                            }`}
                          >
                            {isPast && <i className="ri-check-line mr-1" />}
                            {s.replace(/_/g, ' ')}
                          </span>
                          {idx < arr.length - 1 && (
                            <i className="ri-arrow-right-line text-gray-300 text-xs" />
                          )}
                        </div>
                      );
                    }
                  )}
                </div>
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={handleAdvance}
                    className="px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 whitespace-nowrap cursor-pointer"
                  >
                    {nextStatusLabel[ncr.status] || 'Advance'}
                  </button>
                  {isEditing ? (
                    <>
                      <button
                        onClick={saveEdit}
                        className="px-3 py-1.5 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700 whitespace-nowrap cursor-pointer"
                      >
                        Save Changes
                      </button>
                      <button
                        onClick={() => setIsEditing(false)}
                        className="px-3 py-1.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 whitespace-nowrap cursor-pointer"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={startEdit}
                      className="px-3 py-1.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-edit-line mr-1" />
                      Edit Details
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Linked Inspection */}
            {linkedInspection && (
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Linked Inspection</p>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Inspection #</span>
                    <span className="text-sm font-medium text-gray-900">{linkedInspection.inspectionNumber}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Type</span>
                    <span className="text-sm text-gray-700">{linkedInspection.type.replace(/_/g, ' ')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Source</span>
                    <span className="text-sm text-gray-700">{linkedInspection.sourceNumber}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-500">Result</span>
                    <span
                      className={`text-sm font-medium ${
                        linkedInspection.status === 'PASSED'
                          ? 'text-emerald-600'
                          : linkedInspection.status === 'FAILED'
                          ? 'text-red-600'
                          : 'text-gray-600'
                      }`}
                    >
                      {linkedInspection.status}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Failed Parameters */}
            {failedParamResults.length > 0 && (
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Failed Parameters</p>
                <div className="bg-red-50 rounded-lg p-4 space-y-2">
                  {failedParamResults.map((r) => (
                    <div key={r.id} className="flex items-center gap-2">
                      <i className="ri-close-circle-line text-red-500" />
                      <span className="text-sm text-gray-700">{r.parameterName}</span>
                      <span className="text-xs text-gray-500">({r.observedValue})</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* NCR Details Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Item</p>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-sm font-medium text-gray-900">{ncr.itemName}</p>
                  {ncr.variantName && (
                    <p className="text-xs text-gray-500 mt-0.5">{ncr.variantName}</p>
                  )}
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Source</p>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-sm font-medium text-gray-900">{ncr.sourceNumber}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{ncr.type} QC</p>
                </div>
              </div>
            </div>

            {/* Defect Info */}
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Defect Information</p>
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-xs text-gray-500">Defect Qty</span>
                    <p className="text-sm font-medium text-gray-900 mt-0.5">
                      {ncr.defectQty} {ncr.unit}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500">Disposition</span>
                    {isEditing ? (
                      <div className="mt-1">
                        <SearchableSelect
                          value={editDisposition}
                          onChange={(val) => setEditDisposition(val as MockNCR['dispositionAction'])}
                          options={dispositionOptions}
                          placeholder="Select Disposition"
                        />
                      </div>
                    ) : (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mt-1 ${dispositionBadge(ncr.dispositionAction)}`}>
                        {ncr.dispositionAction.replace(/_/g, ' ')}
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <span className="text-xs text-gray-500">Defect Description</span>
                  <p className="text-sm text-gray-700 mt-0.5">{ncr.defectDescription}</p>
                </div>
              </div>
            </div>

            {/* Assignment */}
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Assignment</p>
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-xs text-gray-500">Assigned To</span>
                    {isEditing ? (
                      <div className="mt-1">
                        <SearchableSelect
                          value={editAssignedTo}
                          onChange={setEditAssignedTo}
                          options={operatorOptions}
                          placeholder="Select Operator"
                          allowClear
                        />
                      </div>
                    ) : (
                      <p className="text-sm font-medium text-gray-900 mt-0.5">
                        {ncr.assignedToName || <span className="text-gray-400">Unassigned</span>}
                      </p>
                    )}
                  </div>
                  <div>
                    <span className="text-xs text-gray-500">Due Date</span>
                    {isEditing ? (
                      <input
                        type="date"
                        value={editDueDate}
                        onChange={(e) => setEditDueDate(e.target.value)}
                        className="mt-1 w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    ) : (
                      <p className="text-sm text-gray-700 mt-0.5">{ncr.dueDate || '—'}</p>
                    )}
                  </div>
                </div>
                <div>
                  <span className="text-xs text-gray-500">Created By</span>
                  <p className="text-sm text-gray-700 mt-0.5">{ncr.createdBy}</p>
                </div>
                {ncr.closedDate && (
                  <div>
                    <span className="text-xs text-gray-500">Closed Date</span>
                    <p className="text-sm text-gray-700 mt-0.5">{ncr.closedDate}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Linked CAPA */}
            {linkedCAPA && (
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Linked CAPA</p>
                <div className="bg-violet-50 rounded-lg p-4 space-y-3 border border-violet-100">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-violet-900">{linkedCAPA.capaNumber}</span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${capaStatusBadge(linkedCAPA.status)}`}>
                      {linkedCAPA.status.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-xs text-gray-500">Type</span>
                      <p className="text-sm text-gray-700 mt-0.5">{linkedCAPA.type.replace(/_/g, ' ')}</p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">Root Cause Category</span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mt-1 ${rootCauseBadge(linkedCAPA.rootCauseCategory)}`}>
                        {linkedCAPA.rootCauseCategory.replace(/_/g, ' ')}
                      </span>
                    </div>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500">Root Cause</span>
                    <p className="text-sm text-gray-700 mt-0.5">{linkedCAPA.rootCause}</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500">Corrective Action</span>
                    <p className="text-sm text-gray-700 mt-0.5">{linkedCAPA.correctiveAction}</p>
                  </div>
                  <div>
                    <span className="text-xs text-gray-500">Preventive Action</span>
                    <p className="text-sm text-gray-700 mt-0.5">{linkedCAPA.preventiveAction}</p>
                  </div>
                  {linkedCAPA.effectiveness && (
                    <div>
                      <span className="text-xs text-gray-500">Effectiveness</span>
                      <p className="text-sm text-gray-700 mt-0.5">{linkedCAPA.effectiveness.replace(/_/g, ' ')}</p>
                    </div>
                  )}
                  {linkedCAPA.verifiedBy && (
                    <div>
                      <span className="text-xs text-gray-500">Verified By</span>
                      <p className="text-sm text-gray-700 mt-0.5">{linkedCAPA.verifiedBy} on {linkedCAPA.verifiedDate}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}